import { AutomationPolicy } from '~/types/automations'
import { TrackingProperties } from '~/utils/tracking'

/**
 * Flattens an AutomationPolicy into Amplitude-friendly top-level properties.
 *
 * Amplitude cannot group by or filter on fields inside arrays. This function
 * denormalizes the `outputs` array into numbered per-swap keys so every field
 * is directly queryable (e.g. group by `swap_0_targetChainId`).
 *
 * It also adds lightweight summary fields (`numSwaps`, `totalSwapPercent`,
 * `targetChainIds`) for quick filtering and chart breakdowns.
 */
export const flattenPolicyForTracking = (
  policy: AutomationPolicy,
): TrackingProperties => {
  const perSwapFields: TrackingProperties = {}

  policy.outputs.forEach((output, index) => {
    perSwapFields[`swap_${index}_percent`] = output.percent
    perSwapFields[`swap_${index}_targetChainId`] = output.targetChainId
    perSwapFields[`swap_${index}_targetTokenAddress`] =
      output.targetTokenAddress
    perSwapFields[`swap_${index}_destinationAddress`] =
      output.destinationAddress
  })

  return {
    numSwaps: policy.outputs.length,
    totalSwapPercent: policy.outputs.reduce(
      (sum, output) => sum + output.percent,
      0,
    ),
    targetChainIds: policy.outputs.map((output) => output.targetChainId),
    slippage: policy.slippage,
    minimumThresholdUsd: policy.minimumThresholdUsd,
    passThroughPercent: policy.passThroughPercent,
    passThroughDestinationAddress: policy.passThroughDestinationAddress,
    ...perSwapFields,
  }
}
